import {Injectable, signal, computed, effect, inject} from '@angular/core';
import {filter, firstValueFrom, take} from 'rxjs';
import dayjs from 'dayjs';
import {HttpErrorResponse} from '@angular/common/http';
import {TranslateService} from '@ngx-translate/core';
import { ZardDialogService } from '../components/ui-components';
import {Limits, LimitsService, SysMsg} from '../../../api';
import {LimitWarning} from '../models';
import {AuthService} from '../../auth.service';
import {MaintenanceStateService} from './maintenance-state.service';
import {UserSeatService} from './user-seat.service';

/**
 * Manages user credit limits and system message popups.
 * Tracks usage against limits with warning thresholds (80% warning, 100% critical)
 * and displays system message popups with read state tracking.
 *
 * @example
 * ```typescript
 * private limitsState = inject(LimitsStateService);
 *
 * // Check if action can be performed
 * if (!this.limitsState.canPerformAction(creditsRequired)) {
 *   // Show limit error
 * }
 *
 * // Get warning based on usage
 * const warning = this.limitsState.limitWarning();
 * if (warning?.status === 'critical') {
 *   // Show critical warning
 * }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class LimitsStateService {
  private limitsService = inject(LimitsService);
  private translator = inject(TranslateService);
  private zardDialog = inject(ZardDialogService);
  private auth = inject(AuthService);
  private maintenanceState = inject(MaintenanceStateService);
  private userSeatService = inject(UserSeatService);


  // Signals
  private _limits = signal<Limits | undefined>(undefined);
  private _systemMessage = signal<SysMsg | undefined>(undefined);
  private _currentPopupKey = signal<string | null | undefined>(undefined);
  private _systemMessageRead = signal(false);
  private _systemMessagePopupOpen = signal(false);
  private _nextLimitReset = signal<Date | undefined>(undefined);

  // Public read-only signals
  readonly limits = this._limits.asReadonly();
  readonly systemMessage = this._systemMessage.asReadonly();
  readonly systemMessageRead = this._systemMessageRead.asReadonly();
  readonly systemMessagePopupOpen = this._systemMessagePopupOpen.asReadonly();
  readonly nextLimitReset = this._nextLimitReset.asReadonly();


  /**
   * A grant running in the current cycle, or undefined when there is none.
   *
   * Undefined rather than 0 so a template can gate on it directly: a breakdown that reads
   * "plan + 0 bonus" is noise on every account that never got one. Everything that enforces a limit
   * keeps reading `max_credits`, which already includes this.
   */
  readonly bonusCredits = computed<number | undefined>(() => {
    const bonus = this._limits()?.bonus_credits;
    return bonus ? bonus : undefined;
  });

  // Computed signals
  /**
   * Whether the next run will be degraded: slower to start, a cheaper model, extraction withheld.
   * From the server, so the banner cannot disagree with what the backend decided at hand-off.
   */
  readonly freeMode = computed(() => this._limits()?.free_mode === true);
  readonly usageBasedActive = computed(() => this._limits()?.usage_based_state === 'usage_based');
  /** False for partners billed outside Stripe (Deubner), which never offer usage-based billing. */
  readonly usageBasedSupported = computed(() => this._limits()?.usage_based_supported !== false);
  readonly usageBasedLimitReached = computed(() =>
    ['firm_limit_reached', 'user_limit_reached'].includes(this._limits()?.usage_based_reason ?? ''));

  readonly exhaustedMessageKey = computed(() => {
    const reason = this._limits()?.usage_based_reason;
    const admin = this.userSeatService.isUserAdminOfLawFirm();
    if (this.usageBasedLimitReached()) {
      return admin ? 'message.usageBasedLimitAdmin' : 'message.usageBasedLimitUser';
    }
    if (reason === 'overage_disabled') {
      return admin ? 'message.usageBasedDisabledAdmin' : 'message.usageBasedDisabledUser';
    }
    if (reason === 'overage_unavailable') {
      return admin ? 'message.usageBasedUnavailableAdmin' : 'message.usageBasedUnavailableUser';
    }
    return 'message.freeModeWarning';
  });

  readonly limitWarning = computed<LimitWarning | undefined>(() => {
    const limits = this._limits();
    if (!limits?.max_credits || limits.usage_based_state === 'usage_based') return undefined;

    const WARNING_MILESTONE = 80;
    const CRITICAL_MILESTONE = 100;
    const currentUsagePercentage = (limits.used_credits / limits.max_credits) * 100;

    if (limits.free_mode) {
      // Says what changed, not only that a number ran out: the user should understand the
      // degradation and upgrade rather than assume the product is broken.
      return {
        icon: 'error',
        message: this.translator.instant(this.exhaustedMessageKey()),
        status: 'critical',
      };
    } else if (currentUsagePercentage >= CRITICAL_MILESTONE) {
      return {
        icon: 'error',
        message: this.translator.instant('message.criticalLimitWarning'),
        status: 'critical',
      };
    } else if (currentUsagePercentage >= WARNING_MILESTONE) {
      return {
        icon: 'warning',
        message: this.translator.instant('message.limitWarning', {
          percentage: Math.round(currentUsagePercentage),
        }),
        status: 'warning',
      };
    }

    return undefined;
  });

  readonly usagePercentage = computed(() => {
    const limits = this._limits();
    if (!limits?.max_credits) return 0;
    // Capped: a degraded run still costs, so the raw figure climbs past the allowance and "142%
    // used" reads as a billing fault.  The server clamps too.
    return Math.min((limits.used_credits / limits.max_credits) * 100, 100);
  });

  readonly hasReachedLimit = computed(() => {
    const percentage = this.usagePercentage();
    return percentage >= 100;
  });

  readonly isNearLimit = computed(() => {
    const percentage = this.usagePercentage();
    return percentage >= 80 && percentage < 100;
  });

  constructor() {
    this.auth.ready$.pipe(
      filter((ready) => ready && !!this.auth.keycloak?.token),
      take(1),
    ).subscribe(() => {
      void this.refreshLimits().catch(() => undefined);
    });

    effect(() => {
      const systemMessage = this._systemMessage();
      const isOpen = this._systemMessagePopupOpen();
      const currentPopupKey = this._currentPopupKey();

      if (!systemMessage || isOpen) return;

      const lastReadKey = localStorage.getItem('systemMessagePopupKeyRead');
      const seenInSessionKey = sessionStorage.getItem('systemMessagePopupKeyRead');
      this._limits();


      if (
        systemMessage.popup_content &&
        currentPopupKey &&
        currentPopupKey !== lastReadKey &&
        currentPopupKey !== seenInSessionKey
      ) {
        void this.showSystemMessagePopup(systemMessage.popup_content, currentPopupKey);
      }
    });
  }

  async refreshLimits(): Promise<{ limits: Limits; sysMsg: SysMsg }> {
    try {
      // Billing status is independent of the system-message endpoint. Publish it first so
      // a failed message request cannot leave the chat banner showing an old decision.
      const limits = await firstValueFrom(this.limitsService.getLimits());
      this._limits.set(limits);
      this._nextLimitReset.set(
        limits.next_reset ? dayjs(limits.next_reset).toDate() : undefined
      );

      const sysMsg = await firstValueFrom(this.limitsService.getSysMsg());
      this.maintenanceState.clear();
      this._systemMessage.set(sysMsg);

      const lastReadSystemMessage = sessionStorage.getItem('systemMessageRead');
      this._systemMessageRead.set(
        !!lastReadSystemMessage && lastReadSystemMessage === sysMsg.message
      );

      this._currentPopupKey.set(sysMsg.popup_key);
      const lastReadPopupKey = localStorage.getItem('systemMessagePopupKeyRead');
      const seenInSessionPopupKey = sessionStorage.getItem('systemMessagePopupKeyRead');
      const popupAlreadyRead = (!!lastReadPopupKey && lastReadPopupKey === sysMsg.popup_key) || (!!seenInSessionPopupKey && seenInSessionPopupKey === sysMsg.popup_key);
      if (
        !popupAlreadyRead &&
        sysMsg.popup_content?.length &&
        !this._systemMessagePopupOpen() &&
        sysMsg.popup_key
      ) {
        void this.showSystemMessagePopup(sysMsg.popup_content, sysMsg.popup_key);
      }

      return {limits, sysMsg};
    } catch (err) {
      if (err instanceof HttpErrorResponse &&
          this.maintenanceState.isMaintenanceError(err) && !this.auth.isStaff) {
        this.maintenanceState.applyFromError(err);
      }
      throw err;
    }
  }
  private async showSystemMessagePopup(content: string, popupKey: string): Promise<void> {
    this._systemMessagePopupOpen.set(true);

    // Loaded on demand: the popup renders markdown, which would pull marked into the startup bundle
    let NewsPopupComponent: typeof import('@new/components/dialogs/news-popup/news-popup.component').NewsPopupComponent;
    try {
      ({ NewsPopupComponent } = await import('@new/components/dialogs/news-popup/news-popup.component'));
    } catch (error) {
      // Stale deployment or network error: allow the next refresh to try again
      console.error('Failed to load news popup', error);
      this._systemMessagePopupOpen.set(false);
      return;
    }
    const dialogRef = NewsPopupComponent.open(
      this.zardDialog,
      content,
      popupKey,
      this.translator.instant('infoPopup.title')
    );

    dialogRef.afterClosed().subscribe(() => {
      this._systemMessagePopupOpen.set(false);
    });
  }

  markSystemMessageAsRead(): void {
    const message = this._systemMessage()?.message;
    if (!message) return;

    sessionStorage.setItem('systemMessageRead', message);
    this._systemMessageRead.set(true);
  }

  // Helper methods for checking limits
  canPerformAction(creditsRequired = 1) {
    const limits = this._limits();
    if (!limits) return true; // If no limits, allow action

    return limits.usage_based_state === 'usage_based' ||
      !!(limits.max_credits && limits.used_credits + creditsRequired <= limits.max_credits);
  }

  getRemainingCreditsInPercentage(): number {
    const limits = this._limits();
    if (!limits || !limits.max_credits) {
      return 0;
    }

    const usedPercentage = (limits.used_credits / limits.max_credits) * 100;

    return Math.min(100, usedPercentage);
  }

  getPercentageUsed(): number {
    const limits = this._limits();
    if (!limits || !limits.max_credits) {
      return 0;
    }

    return (limits.used_credits / limits.max_credits) * 100;
  }

  getTimeUntilReset(): string | null {
    const resetDate = this._nextLimitReset();
    if (!resetDate) return null;

    const now = dayjs();
    const reset = dayjs(resetDate);
    const diff = reset.diff(now);

    if (diff < 0) return null;

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return this.translator.instant('limits.resetIn.days', {days});
    } else if (hours > 0) {
      return this.translator.instant('limits.resetIn.hours', {hours});
    } else {
      return this.translator.instant('limits.resetIn.minutes', {minutes});
    }
  }

  /**
   * Get formatted trial phase end date
   * @param format - Date format string (default: 'DD.MM.YYYY HH:mm')
   * @returns Formatted date string or null if not in trial phase
   */
  getTrialPhaseEndDate(format = 'DD.MM.YYYY HH:mm'): string | null {
    const limits = this._limits();
    if (!limits?.trial_phase_ends_on) return null;
    return dayjs(limits.trial_phase_ends_on).format(format);
  }

  /**
   * Get formatted next reset/renewal date
   * @param format - Date format string (default: 'DD.MM.YYYY HH:mm')
   * @returns Formatted date string or null if not available
   */
  getNextResetDate(format = 'DD.MM.YYYY HH:mm'): string | null {
    const limits = this._limits();
    if (!limits?.next_reset) return null;
    return dayjs(limits.next_reset).format(format);
  }

  /** End of the current billing period (a year on yearly plans), when scheduled removals take effect. */
  getNextBillingDate(format = 'DD.MM.YYYY'): string | null {
    const limits = this._limits();
    if (!limits?.next_billing_date) return null;
    return dayjs(limits.next_billing_date).format(format);
  }

  /**
   * Check if user is currently in trial phase
   * @returns true if trial_phase_ends_on is set and in the future
   */
  isInTrialPhase(): boolean {
    const limits = this._limits();
    if (!limits?.trial_phase_ends_on) return false;
    return dayjs(limits.trial_phase_ends_on).isAfter(dayjs());
  }

  /**
   * Get the renewal date - returns trial end date if in trial, otherwise next reset date
   * @param format - Date format string (default: 'DD.MM.YYYY HH:mm')
   * @returns Formatted date string or null
   */
  getRenewalDate(format = 'DD.MM.YYYY HH:mm'): string | null {
    if (this.isInTrialPhase()) {
      return this.getTrialPhaseEndDate(format);
    }
    return this.getNextResetDate(format);
  }

  async setSystemMessage(message: string): Promise<void> {
    await firstValueFrom(this.limitsService.updateSysMsg({
      sysMsg: {
        message: message,
        popup_content: this._systemMessage()?.popup_content,
        popup_key: this._systemMessage()?.popup_key,
      }
    }))
    this._systemMessage.set({
      ...this._systemMessage(),
      message: message,
    });
    const lastRead = sessionStorage.getItem('systemMessageRead');
    this._systemMessageRead.set(!!lastRead && lastRead === message);
  }

  async checkMaintenanceEnded(): Promise<void> {
    return this.maintenanceState.checkMaintenanceEnded();
  }

  async setSystemPopup(content: string, key: string): Promise<void> {
    await firstValueFrom(this.limitsService.updateSysMsg({
      sysMsg: {
        message: this._systemMessage()?.message,
        popup_content: content,
        popup_key: key
      }
    }))
    this._systemMessage.set({
      ...this._systemMessage(),
      popup_content: content,
      popup_key: key,
    });
  }

  async setEnforceMfa(enabled: boolean): Promise<void> {
    await firstValueFrom(this.limitsService.updateSettings({
      settings: {settings: {enforce_mfa: enabled}},
    }));
    const current = this._limits();
    if (current) {
      this._limits.set({
        ...current,
        settings: {...current.settings, enforce_mfa: enabled},
      });
    }
  }
}
